/**
 * The graph store: a Map of immutable node records plus a sorted children index.
 *
 * Pattern: "immutable records in a mutable index". A change replaces one record and, for moves,
 * one or two children arrays (copy-on-write), so UI layers can compare by reference and re-render
 * only what changed, while the store itself never copies 200k nodes.
 *
 * The children index includes tombstones. New keys are generated between adjacent entries of the
 * full list, so they can never collide with a deleted sibling that is later restored.
 *
 * `apply` is deterministic: the same state and op give the same result on client and server.
 * It validates preconditions first and mutates only when the op is valid, and returns the inverse.
 */
import type { NodeId } from './ids.ts'
import type { Node } from './model.ts'
import { op, type OpBody } from './ops.ts'
import { compareSiblings } from './order.ts'
import { relinkContent } from './refs.ts'

export class OpError extends Error {
  readonly body: OpBody | undefined
  constructor(message: string, body?: OpBody) {
    super(message)
    this.name = 'OpError'
    this.body = body
  }
}

export interface ChangeSet {
  /** Nodes whose record changed. */
  readonly nodes: ReadonlySet<NodeId>
  /** True if tree shape or visibility changed (create, move, collapse, delete, restore). */
  readonly structural: boolean
}

type Listener = (changes: ChangeSet) => void

const EMPTY: readonly NodeId[] = Object.freeze([])

export class Graph {
  private readonly records = new Map<NodeId, Node>()
  private readonly kids = new Map<NodeId, readonly NodeId[]>()
  private readonly listeners = new Set<Listener>()
  private pendingNodes = new Set<NodeId>()
  private pendingStructural = false
  private batchDepth = 0
  /** Incremented on every structural change; cheap memo key for flattened views. */
  structureVersion = 0
  /** Prior records of touched nodes, for exact rollback (see beginTrack). */
  private trackers: Map<NodeId, Node | undefined>[] = []

  // ---------------------------------------------------------------- reads

  get size(): number {
    return this.records.size
  }

  get(id: NodeId): Node | undefined {
    return this.records.get(id)
  }

  must(id: NodeId): Node {
    const n = this.records.get(id)
    if (!n) throw new OpError(`no such node ${id}`)
    return n
  }

  has(id: NodeId): boolean {
    return this.records.has(id)
  }

  /** Live = exists and not deleted. Because deletes cascade, a live node's ancestors are live too. */
  isLive(id: NodeId): boolean {
    const n = this.records.get(id)
    return n !== undefined && n.deletedAt === null
  }

  /** All children, tombstones included, sorted by (order, id). Returns a stable array reference. */
  children(id: NodeId): readonly NodeId[] {
    return this.kids.get(id) ?? EMPTY
  }

  nodes(): IterableIterator<Node> {
    return this.records.values()
  }

  parentIds(): IterableIterator<NodeId> {
    return this.kids.keys()
  }

  // ---------------------------------------------------------------- bulk load

  /** Replace the whole graph (snapshot load). Sorts each children list once: O(n log n). */
  load(nodes: Iterable<Node>): void {
    this.records.clear()
    this.kids.clear()
    const lists = new Map<NodeId, NodeId[]>()
    for (const n of nodes) {
      this.records.set(n.id, n)
      if (n.parentId !== null) {
        let l = lists.get(n.parentId)
        if (!l) lists.set(n.parentId, (l = []))
        l.push(n.id)
      }
    }
    for (const [pid, l] of lists) {
      l.sort((a, b) => {
        const x = this.records.get(a)!
        const y = this.records.get(b)!
        return compareSiblings(x.order, x.id, y.order, y.id)
      })
      this.kids.set(pid, l)
    }
    this.structureVersion++
    this.emit(new Set(this.records.keys()), true)
  }

  // ---------------------------------------------------------------- subscriptions

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  private emit(nodes: Set<NodeId>, structural: boolean): void {
    if (nodes.size === 0 && !structural) return
    const cs: ChangeSet = { nodes, structural }
    for (const l of this.listeners) l(cs)
  }

  private touch(id: NodeId, structural: boolean): void {
    this.pendingNodes.add(id)
    if (structural) this.pendingStructural = true
  }

  private flush(): void {
    if (this.batchDepth > 0) return
    const nodes = this.pendingNodes
    const structural = this.pendingStructural
    this.pendingNodes = new Set()
    this.pendingStructural = false
    if (structural) this.structureVersion++
    this.emit(nodes, structural)
  }

  // ---------------------------------------------------------------- exact rollback

  /**
   * Start recording the prior record (or absence) of every node an apply touches. Rolling back
   * with restoreRecords is exact: a created node disappears instead of becoming a tombstone, which
   * is what a local rollback needs (undo, by contrast, logs inverse ops). Trackers nest.
   */
  beginTrack(): void {
    this.trackers.push(new Map())
  }

  endTrack(): Map<NodeId, Node | undefined> {
    const t = this.trackers.pop()
    if (!t) throw new Error('endTrack without beginTrack')
    return t
  }

  private note(id: NodeId): void {
    for (const t of this.trackers) if (!t.has(id)) t.set(id, this.records.get(id))
  }

  /** Put records back exactly as tracked (undefined = the node did not exist). */
  restoreRecords(saved: ReadonlyMap<NodeId, Node | undefined>): void {
    this.batchDepth++
    try {
      for (const [id, rec] of saved) {
        this.note(id)
        const cur = this.records.get(id)
        if (cur && cur.parentId !== null) this.removeKidIfPresent(cur.parentId, id)
        if (rec === undefined) this.records.delete(id)
        else {
          this.records.set(id, rec)
          if (rec.parentId !== null) this.insertKid(rec.parentId, rec)
        }
        this.touch(id, true)
      }
    } finally {
      this.batchDepth--
      this.flush()
    }
  }

  /** Run `fn` with listener notifications batched into one. */
  batch<T>(fn: () => T): T {
    this.batchDepth++
    try {
      return fn()
    } finally {
      this.batchDepth--
      this.flush()
    }
  }

  // ---------------------------------------------------------------- writes

  /**
   * Apply ops in order, atomically: if one fails, every touched record is restored exactly (no
   * tombstones left behind) and the error is rethrown. Returns the inverse of each op, in order.
   * Listeners are notified once per batch.
   */
  applyBatch(ops: readonly OpBody[], ts: number): OpBody[] {
    const inverses: OpBody[] = []
    this.batchDepth++
    this.beginTrack()
    try {
      for (const o of ops) inverses.push(this.applyOne(o, ts))
      this.endTrack()
    } catch (e) {
      this.restoreRecords(this.endTrack())
      throw e
    } finally {
      this.batchDepth--
      this.flush()
    }
    return inverses
  }

  apply(o: OpBody, ts: number): OpBody {
    return this.applyBatch([o], ts)[0]!
  }

  private liveOrThrow(id: NodeId, o: OpBody, what = 'node'): Node {
    const n = this.records.get(id)
    if (!n) throw new OpError(`${o.type}: ${what} ${id} does not exist`, o)
    if (n.deletedAt !== null) throw new OpError(`${o.type}: ${what} ${id} is deleted`, o)
    return n
  }

  private applyOne(o: OpBody, ts: number): OpBody {
    switch (o.type) {
      case 'node.create': {
        const n = o.payload.node
        if (n.id !== o.nodeId) throw new OpError('node.create: id mismatch', o)
        if (this.records.has(n.id)) throw new OpError(`node.create: ${n.id} exists`, o)
        if (n.deletedAt !== null) throw new OpError('node.create: created deleted', o)
        if (n.parentId === null) {
          if (n.kind === 'block') throw new OpError('node.create: block without parent', o)
        } else {
          this.liveOrThrow(n.parentId, o, 'parent')
        }
        const rec: Node = { ...n, updatedAt: ts }
        this.note(n.id)
        this.records.set(n.id, rec)
        if (n.parentId !== null) this.insertKid(n.parentId, rec)
        this.touch(n.id, true)
        return op.delete(n.id)
      }

      case 'node.content': {
        const n = this.liveOrThrow(o.nodeId, o)
        this.note(n.id)
        this.records.set(n.id, { ...n, content: o.payload.content, updatedAt: ts })
        this.touch(n.id, false)
        return op.content(n.id, n.content)
      }

      case 'node.relink': {
        const n = this.liveOrThrow(o.nodeId, o)
        const content = relinkContent(n.content, o.payload.from, o.payload.to)
        if (content !== n.content) {
          this.note(n.id)
          this.records.set(n.id, { ...n, content, updatedAt: ts })
          this.touch(n.id, false)
        }
        return op.content(n.id, n.content)
      }

      case 'node.collapse': {
        const n = this.liveOrThrow(o.nodeId, o)
        this.note(n.id)
        this.records.set(n.id, { ...n, collapsed: o.payload.collapsed, updatedAt: ts })
        this.touch(n.id, true)
        return op.collapse(n.id, n.collapsed)
      }

      case 'node.title': {
        const n = this.liveOrThrow(o.nodeId, o)
        if (n.kind === 'block') throw new OpError('node.title: blocks have no title', o)
        this.note(n.id)
        this.records.set(n.id, { ...n, title: o.payload.title, updatedAt: ts })
        this.touch(n.id, false)
        return op.title(n.id, n.title)
      }

      case 'node.props': {
        const n = this.liveOrThrow(o.nodeId, o)
        const { field, value } = o.payload
        const old = Object.prototype.hasOwnProperty.call(n.props, field) ? n.props[field] : null
        const props: Record<string, unknown> = { ...n.props }
        if (value === null || value === undefined) delete props[field]
        else props[field] = value
        this.note(n.id)
        this.records.set(n.id, { ...n, props, updatedAt: ts })
        this.touch(n.id, false)
        return op.prop(n.id, field, old)
      }

      case 'node.tags': {
        const n = this.liveOrThrow(o.nodeId, o)
        this.note(n.id)
        this.records.set(n.id, { ...n, tags: [...o.payload.tags], updatedAt: ts })
        this.touch(n.id, false)
        return op.tags(n.id, [...n.tags])
      }

      case 'node.move': {
        const n = this.liveOrThrow(o.nodeId, o)
        const { parentId, order } = o.payload
        if (n.parentId === null) throw new OpError('node.move: top-level nodes do not move', o)
        this.liveOrThrow(parentId, o, 'target parent')
        // Cycle check: the target must not be the node or one of its descendants.
        for (let cur: NodeId | null = parentId; cur !== null; cur = this.records.get(cur)!.parentId) {
          if (cur === n.id) throw new OpError('node.move: would create a cycle', o)
        }
        this.note(n.id)
        this.removeKid(n.parentId, n.id)
        const rec: Node = { ...n, parentId, order, updatedAt: ts }
        this.records.set(n.id, rec)
        this.insertKid(parentId, rec)
        this.touch(n.id, true)
        return op.move(n.id, n.parentId, n.order)
      }

      case 'node.delete': {
        this.liveOrThrow(o.nodeId, o)
        const ids = this.liveSubtree(o.nodeId)
        for (const id of ids) {
          const m = this.records.get(id)!
          this.note(id)
          this.records.set(id, { ...m, deletedAt: ts, updatedAt: ts })
          this.touch(id, true)
        }
        return op.restore(o.nodeId, ids)
      }

      case 'node.restore': {
        const root = this.records.get(o.nodeId)
        if (!root || root.deletedAt === null) throw new OpError('node.restore: root is not deleted', o)
        const ids = o.payload.ids
        const set = new Set(ids)
        if (!set.has(o.nodeId)) throw new OpError('node.restore: ids must include the root', o)
        // Validate first: every restored node is deleted, and its parent is live or restored here.
        for (const id of ids) {
          const m = this.records.get(id)
          if (!m || m.deletedAt === null) throw new OpError(`node.restore: ${id} is not deleted`, o)
          if (m.parentId !== null && !set.has(m.parentId) && !this.isLive(m.parentId)) {
            throw new OpError(`node.restore: parent of ${id} is deleted`, o)
          }
        }
        for (const id of ids) {
          const m = this.records.get(id)!
          this.note(id)
          this.records.set(id, { ...m, deletedAt: null, updatedAt: ts })
          this.touch(id, true)
        }
        return op.delete(o.nodeId)
      }
    }
  }

  /** The node and all live descendants, pre-order. */
  liveSubtree(id: NodeId): NodeId[] {
    const out: NodeId[] = []
    const stack: NodeId[] = [id]
    while (stack.length) {
      const cur = stack.pop()!
      out.push(cur)
      const ks = this.kids.get(cur)
      if (!ks) continue
      for (let i = ks.length - 1; i >= 0; i--) {
        if (this.records.get(ks[i]!)!.deletedAt === null) stack.push(ks[i]!)
      }
    }
    return out
  }

  private insertKid(parentId: NodeId, rec: Node): void {
    const cur = this.kids.get(parentId) ?? EMPTY
    let lo = 0
    let hi = cur.length
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      const m = this.records.get(cur[mid]!)!
      if (compareSiblings(m.order, m.id, rec.order, rec.id) < 0) lo = mid + 1
      else hi = mid
    }
    const next = cur.slice()
    next.splice(lo, 0, rec.id)
    this.kids.set(parentId, next)
    this.touch(parentId, true)
  }

  private removeKidIfPresent(parentId: NodeId, id: NodeId): void {
    const cur = this.kids.get(parentId)
    const i = cur ? cur.indexOf(id) : -1
    if (!cur || i < 0) return
    this.setKids(parentId, cur, i)
  }

  /** Remove entry `i`; an emptied list is dropped so the index never names vanished parents. */
  private setKids(parentId: NodeId, cur: readonly NodeId[], i: number): void {
    if (cur.length === 1) this.kids.delete(parentId)
    else {
      const next = cur.slice()
      next.splice(i, 1)
      this.kids.set(parentId, next)
    }
    this.touch(parentId, true)
  }

  private removeKid(parentId: NodeId, id: NodeId): void {
    const cur = this.kids.get(parentId)
    const i = cur ? cur.indexOf(id) : -1
    if (!cur || i < 0) throw new OpError(`index corrupt: ${id} not under ${parentId}`)
    this.setKids(parentId, cur, i)
  }
}

